import { useEffect, useState, useRef } from "react";
import api from "../api";
import Sidebar from "../Components/Sidebar";
import Topbar from "../Components/Topbar";
import DeliveryChallanTemplate from "./DeliveryChallanTemplate";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import "../CSS/Deliverychallan.css";

function DeliveryHistory() {
  const [historyList, setHistoryList] = useState([]);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Which row's date is being edited and its temp value
  const [editingDateRow, setEditingDateRow] = useState(null); // DeliveryChallanNo
  const [editingDateValue, setEditingDateValue] = useState("");

  const printRef = useRef();

  useEffect(() => {
    fetchHistory();
  }, []);

  /* DATE FORMAT — returns YYYY-MM-DD */
  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const str = String(dateValue);
    if (str.includes("T")) return str.split("T")[0];
    const date = new Date(str);
    if (isNaN(date)) return "-";
    return date.toISOString().split("T")[0];
  };

  /* Display as DD/MM/YYYY */
  const displayDate = (dateValue) => {
    const iso = formatDate(dateValue);
    if (iso === "-") return "-";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get("/delivery/getDeliveryChallanHistory");
      if (res.data.status) setHistoryList(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  /* ================= VIEW ================= */
  const viewChallan = async (challanNo) => {
    try {
      const res = await api.get(`/delivery/getChallanByNumber/${challanNo}`);
      if (res.data.status) {
        const data = res.data.data;

        // Use the edited date from historyList if available
        const rowDate = historyList.find(
          (r) => r.DeliveryChallanNo === challanNo
        )?.created_date;

        setSelectedChallan({
          recipientName: data[0]?.customer_name,
          recipientAddress: data[0]?.address || "",
          orderNo: challanNo,
          date: formatDate(rowDate || data[0]?.created_date),
          items: data.map((item) => ({
            name: item.product_name,
            quantity: item.quantity,
          })),
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  /* ================= INLINE DATE EDIT ================= */
  const startEditDate = (challanNo, currentDate) => {
    setEditingDateRow(challanNo);
    setEditingDateValue(formatDate(currentDate));
  };

  const confirmDateEdit = (challanNo) => {
    if (!editingDateValue) return;

    // Update historyList locally
    setHistoryList((prev) =>
      prev.map((row) =>
        row.DeliveryChallanNo === challanNo
          ? { ...row, created_date: editingDateValue }
          : row
      )
    );

    // If this challan is currently open in preview, update the date there too
    if (selectedChallan?.orderNo === challanNo) {
      setSelectedChallan((prev) => ({ ...prev, date: editingDateValue }));
    }

    setEditingDateRow(null);
    setEditingDateValue("");
  };

  const cancelDateEdit = () => {
    setEditingDateRow(null);
    setEditingDateValue("");
  };

  /* ================= DELETE ================= */
  const deleteChallan = async (challanNo) => {
    if (!window.confirm("Are you sure you want to delete this challan?")) return;
    try {
      const res = await api.post("/delivery/deleteChallan", {
        challan_no: challanNo,
      });
      if (res.data.status) {
        alert("Challan deleted successfully");
        fetchHistory();
        setSelectedChallan(null);
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Error deleting challan");
    }
  };

  /* ================= DOWNLOAD PDF ================= */
  const downloadPDF = async () => {
    if (!printRef.current) return alert("Nothing to download");

    try {
      // Read the watermark's actual rendered size & position BEFORE hiding it
      // Use offsetWidth/offsetHeight — gives pre-transform size (actual image size)
      // getBoundingClientRect includes rotation which distorts the measurement
      const wmEl = printRef.current.querySelector(".dc-watermark-preview");
      let wmRect = null;
      let containerRect = null;
      if (wmEl) {
        const containerW = printRef.current.offsetWidth;
        const containerH = printRef.current.offsetHeight;
        const wmW_css    = wmEl.offsetWidth;
        const wmH_css    = wmEl.offsetHeight;
        // Center position from CSS: top:50%, left:50%, translate(-50%,-50%)
        wmRect        = { width: wmW_css, height: wmH_css };
        containerRect = { width: containerW, height: containerH, left: 0, top: 0 };
        // Store center as half of container
        wmRect.centerX = containerW / 2;
        wmRect.centerY = containerH / 2;
      }

      // Hide preview watermark so html2canvas doesn't capture it (avoids 2nd page)
      printRef.current.classList.add("dc-capturing");

      const scale = 2; // must match html2canvas scale below
      const canvas = await html2canvas(printRef.current, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        windowWidth: printRef.current.scrollWidth,
        windowHeight: printRef.current.scrollHeight,
      });

      // Restore watermark in preview
      printRef.current.classList.remove("dc-capturing");

      // Composite watermark at EXACT same size/position as the CSS preview
      const wmImg = new Image();
      wmImg.crossOrigin = "anonymous";
      wmImg.src = `${window.location.origin}/andavarwt.png`;
      await new Promise((res) => { wmImg.onload = res; wmImg.onerror = res; });

      if (wmImg.complete && wmImg.naturalWidth > 0) {
        const ctx = canvas.getContext("2d");

        // Use DOM-measured size × scale to match preview exactly
        const wmW = wmRect ? wmRect.width  * scale : canvas.width  * 0.35;
        const wmH = wmRect ? wmRect.height * scale : (wmImg.naturalHeight / wmImg.naturalWidth) * wmW;

        // Center: always use canvas center (CSS uses top:50% left:50% translate(-50%,-50%))
        const cx = wmRect ? wmRect.centerX * scale : canvas.width  / 2;
        const cy = wmRect ? wmRect.centerY * scale : canvas.height / 2;

        ctx.save();
        ctx.globalAlpha = 0.07;
        ctx.translate(cx, cy);
        ctx.rotate((-15 * Math.PI) / 180);
        ctx.drawImage(wmImg, -wmW / 2, -wmH / 2, wmW, wmH);
        ctx.restore();
      }

      // Single-page PDF
      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const pdf     = new jsPDF("p", "mm", "a4");
      const pageW   = 210;
      const pageH   = 297;
      const imgH    = (canvas.height * pageW) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pageW, imgH <= pageH ? imgH : pageH);
      pdf.save(`Challan_${selectedChallan?.orderNo || "file"}.pdf`);

    } catch (err) {
      printRef.current?.classList.remove("dc-capturing");
      console.error("PDF error:", err);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  /* ================= PRINT ================= */
  const handlePrint = () => {
    if (!printRef.current) return;

    // Open a clean isolated window with only the challan template
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return alert("Pop-up blocked. Please allow pop-ups and try again.");

    // Collect all stylesheet links from the current document
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((el) => el.outerHTML)
      .join("\n");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Delivery Challan - ${selectedChallan?.orderNo || ""}</title>
          ${styles}
          <style>
            @page { size: A4 portrait; margin: 0; }
            body { margin: 0; padding: 0; background: #fff; }
            /* Ensure only the challan fits one page */
            .dc-page { page-break-after: avoid; break-after: avoid; }
          </style>
        </head>
        <body>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for resources (images, fonts) to load before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  /* ================= SEARCH ================= */
  const filteredHistory = historyList.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.DeliveryChallanNo?.toLowerCase().includes(search) ||
      item.customer_name?.toLowerCase().includes(search) ||
      item.created_date?.toLowerCase().includes(search) ||
      item.total_items?.toString().includes(search) ||
      item.total_quantity?.toString().includes(search)
    );
  });

  return (
    <div className="sales-page">
      <Sidebar />
      <Topbar />

      <div className="sales-header">
        <h1>Delivery Challan History</h1>
      </div>

      <div className="sales-table-card">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>History</h2>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "10px", width: "300px" }}
          />
        </div>

        <table className="sales-table">
          <thead>
            <tr>
              <th>SL No</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total Items</th>
              <th>Total Quantity</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.length === 0 ? (
              <tr>
                <td colSpan="6">No Challans Found</td>
              </tr>
            ) : (
              filteredHistory.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.customer_name}</td>

                  {/* ── INLINE EDITABLE DATE CELL ── */}
                  <td>
                    {editingDateRow === item.DeliveryChallanNo ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <input
                          type="date"
                          value={editingDateValue}
                          onChange={(e) => setEditingDateValue(e.target.value)}
                          style={{ padding: "4px 6px", fontSize: "13px", borderRadius: "4px", border: "1px solid #4f46e5" }}
                          autoFocus
                        />
                        <button
                          onClick={() => confirmDateEdit(item.DeliveryChallanNo)}
                          style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer" }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelDateEdit}
                          style={{ background: "#e0e0e0", color: "#333", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer" }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span>{displayDate(item.created_date)}</span>
                        <button
                          onClick={() => startEditDate(item.DeliveryChallanNo, item.created_date)}
                          title="Edit date"
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#4f46e5", padding: "0" }}
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>

                  <td>{item.total_items}</td>
                  <td>{item.total_quantity}</td>
                  <td>
                    <button onClick={() => viewChallan(item.DeliveryChallanNo)}>
                      View
                    </button>
                    <button
                      onClick={() => deleteChallan(item.DeliveryChallanNo)}
                      style={{ marginLeft: "10px", backgroundColor: "red", color: "#fff" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── PREVIEW ── */}
      {selectedChallan && (
        <div className="sales-table-card">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h2>Challan Preview</h2>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={downloadPDF}>Download PDF</button>
              <button onClick={handlePrint}>Print</button>
              <button onClick={() => setSelectedChallan(null)}>Close</button>
            </div>
          </div>

          <div ref={printRef}>
            <DeliveryChallanTemplate challanData={selectedChallan} />
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryHistory;




















// import React, { useEffect, useState, useRef } from "react";
// import api from "../api";
// import Sidebar from "../Components/Sidebar";
// import Topbar from "../Components/Topbar";
// import DeliveryChallanTemplate from "./DeliveryChallanTemplate";
// import html2pdf from "html2pdf.js";

// import "../CSS/Deliverychallan.css";

// function DeliveryHistory() {
//   const [historyList, setHistoryList] = useState([]);
//   const [selectedChallan, setSelectedChallan] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");

//   const printRef = useRef();

//   useEffect(() => {
//     fetchHistory();
//   }, []);

//   const fetchHistory = async () => {
//     try {
//       const res = await api.get("/delivery/getDeliveryChallanHistory");
//       if (res.data.status) setHistoryList(res.data.data);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // ✅ VIEW FUNCTION
//   const viewChallan = async (challanNo) => {
//     try {
//       const res = await api.get(`/delivery/getChallanByNumber/${challanNo}`);

//       if (res.data.status) {
//         const data = res.data.data;

//         const formattedData = {
//           recipientName: data[0]?.customer_name,
//           recipientAddress: data[0]?.address || "",
//           orderNo: challanNo,
//           date: data[0]?.created_date,
//           items: data.map((item) => ({
//             name: item.product_name,
//             quantity: item.quantity,
//           })),
//         };

//         setSelectedChallan(formattedData);
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // ✅ DELETE FUNCTION
//   const deleteChallan = async (challanNo) => {
//     if (!window.confirm("Are you sure you want to delete this challan?")) return;

//     try {
//       const res = await api.post("/delivery/deleteChallan", {
//         challan_no: challanNo
//       });

//       if (res.data.status) {
//         alert("Challan deleted successfully");
//         fetchHistory(); // refresh table
//         setSelectedChallan(null); // close preview
//       } else {
//         alert(res.data.message);
//       }
//     } catch (err) {
//       console.error("Delete Error:", err);
//       alert("Error deleting challan");
//     }
//   };

//   // ✅ DOWNLOAD PDF
//   const downloadPDF = () => {
//     if (!printRef.current) {
//       alert("Nothing to download");
//       return;
//     }

//     const opt = {
//       margin: 10,
//       filename: `Challan_${selectedChallan?.orderNo || "file"}.pdf`,
//       image: { type: "jpeg", quality: 0.98 },
//       html2canvas: { scale: 2, useCORS: true },
//       jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
//     };

//     html2pdf().set(opt).from(printRef.current).save();
//   };

//   // ✅ PRINT
//   const handlePrint = () => {
//     const content = printRef.current.innerHTML;
//     const newWindow = window.open("", "", "width=900,height=700");

//     newWindow.document.write(`
//       <html>
//         <head>
//           <title>Challan</title>
//         </head>
//         <body>
//           ${content}
//         </body>
//       </html>
//     `);

//     newWindow.document.close();
//     newWindow.print();
//   };

//   // ✅ SEARCH FILTER
//   const filteredHistory = historyList.filter((item) => {
//     const search = searchTerm.toLowerCase();

//     return (
//       item.DeliveryChallanNo?.toLowerCase().includes(search) ||
//       item.customer_name?.toLowerCase().includes(search) ||
//       item.created_date?.toLowerCase().includes(search) ||
//       item.total_items?.toString().includes(search) ||
//       item.total_quantity?.toString().includes(search)
//     );
//   });

//   return (
//     <div className="sales-page">
//       <Sidebar />
//       <Topbar />

//       <div className="sales-header">
//         <h1>Delivery Challan History</h1>
//       </div>

//       <div className="sales-table-card">
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//           <h2>History</h2>

//           <input
//             type="text"
//             placeholder="Search..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             style={{
//               padding: "10px",
//               borderRadius: "5px",
//               border: "1px solid #ccc",
//               width: "300px"
//             }}
//           />
//         </div>

//         <table className="sales-table">
//           <thead>
//             <tr>
//               <th>Challan No</th>
//               <th>Customer</th>
//               <th>Date</th>
//               <th>Total Items</th>
//               <th>Total Quantity</th>
//               <th>Action</th>
//             </tr>
//           </thead>

//           <tbody>
//             {filteredHistory.length === 0 ? (
//               <tr>
//                 <td colSpan="6">No Challans Found</td>
//               </tr>
//             ) : (
//               filteredHistory.map((item, index) => (
//                 <tr key={index}>
//                   <td>{item.DeliveryChallanNo}</td>
//                   <td>{item.customer_name}</td>
//                   <td>{item.created_date}</td>
//                   <td>{item.total_items}</td>
//                   <td>{item.total_quantity}</td>

//                   <td>
//                     <button onClick={() => viewChallan(item.DeliveryChallanNo)}>
//                       View
//                     </button>

//                     <button
//                       onClick={() => deleteChallan(item.DeliveryChallanNo)}
//                       style={{
//                         marginLeft: "10px",
//                         backgroundColor: "red",
//                         color: "#fff"
//                       }}
//                     >
//                       Delete
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* ✅ PREVIEW */}
//       {selectedChallan && (
//         <div className="sales-table-card">
//           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
//             <h2>Challan Preview</h2>

//             <div style={{ display: "flex", gap: "10px" }}>
//               <button onClick={downloadPDF}>Download PDF</button>
//               <button onClick={handlePrint}>Print</button>
//               <button onClick={() => setSelectedChallan(null)}>Close</button>
//             </div>
//           </div>

//           <div ref={printRef}>
//             <DeliveryChallanTemplate challanData={selectedChallan} />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default DeliveryHistory;








// import React, { useEffect, useState, useRef } from "react";
// import api from "../api";
// import Sidebar from "../Components/Sidebar";
// import Topbar from "../Components/Topbar";
// import DeliveryChallanTemplate from "./DeliveryChallanTemplate";
// import html2pdf from "html2pdf.js";

// import "../CSS/Deliverychallan.css";

// function DeliveryHistory() {
//   const [historyList, setHistoryList] = useState([]);
//   const [selectedChallan, setSelectedChallan] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");

//   const printRef = useRef();

//   useEffect(() => {
//     fetchHistory();
//   }, []);

//   const fetchHistory = async () => {
//     try {
//       const res = await api.get("/delivery/getDeliveryChallanHistory");
//       if (res.data.status) setHistoryList(res.data.data);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // ✅ VIEW FUNCTION
//   const viewChallan = async (challanNo) => {
//     try {
//       const res = await api.get(`/delivery/getChallanByNumber/${challanNo}`);

//       if (res.data.status) {
//         const data = res.data.data;

//         const formattedData = {
//           recipientName: data[0]?.customer_name,
//           recipientAddress: data[0]?.address || "",
//           orderNo: challanNo,
//           date: data[0]?.created_date,
//           items: data.map((item) => ({
//             name: item.product_name,
//             quantity: item.quantity,
//           })),
//         };

//         setSelectedChallan(formattedData);
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // ✅ DOWNLOAD PDF (FIXED)
//   const downloadPDF = () => {
//     if (!printRef.current) {
//       alert("Nothing to download");
//       return;
//     }

//     const opt = {
//       margin: 10,
//       filename: `Challan_${selectedChallan?.orderNo || "file"}.pdf`,
//       image: { type: "jpeg", quality: 0.98 },
//       html2canvas: {
//         scale: 2,
//         useCORS: true,
//       },
//       jsPDF: {
//         unit: "mm",
//         format: "a4",
//         orientation: "portrait",
//       },
//     };

//     html2pdf().set(opt).from(printRef.current).save();
//   };

//   // ✅ PRINT OPTION (RECOMMENDED BACKUP)
//   const handlePrint = () => {
//     const content = printRef.current.innerHTML;
//     const newWindow = window.open("", "", "width=900,height=700");

//     newWindow.document.write(`
//       <html>
//         <head>
//           <title>Challan</title>
//         </head>
//         <body>
//           ${content}
//         </body>
//       </html>
//     `);

//     newWindow.document.close();
//     newWindow.print();
//   };

//   // ✅ SEARCH FILTER
//   const filteredHistory = historyList.filter((item) => {
//     const search = searchTerm.toLowerCase();

//     return (
//       item.DeliveryChallanNo?.toLowerCase().includes(search) ||
//       item.customer_name?.toLowerCase().includes(search) ||
//       item.created_date?.toLowerCase().includes(search) ||
//       item.total_items?.toString().includes(search) ||
//       item.total_quantity?.toString().includes(search)
//     );
//   });

//   return (
//     <div className="sales-page">
//       <Sidebar />
//       <Topbar />

//       <div className="sales-header">
//         <h1>Delivery Challan History</h1>
//       </div>

//       <div className="sales-table-card">
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//           <h2>History</h2>

//           <input
//             type="text"
//             placeholder="Search..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             style={{
//               padding: "10px",
//               borderRadius: "5px",
//               border: "1px solid #ccc",
//               width: "300px"
//             }}
//           />
//         </div>

//         <table className="sales-table">
//           <thead>
//             <tr>
//               <th>Challan No</th>
//               <th>Customer</th>
//               <th>Date</th>
//               <th>Total Items</th>
//               <th>Total Quantity</th>
//               <th>Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {filteredHistory.length === 0 ? (
//               <tr>
//                 <td colSpan="6">No Challans Found</td>
//               </tr>
//             ) : (
//               filteredHistory.map((item, index) => (
//                 <tr key={index}>
//                   <td>{item.DeliveryChallanNo}</td>
//                   <td>{item.customer_name}</td>
//                   <td>{item.created_date}</td>
//                   <td>{item.total_items}</td>
//                   <td>{item.total_quantity}</td>
//                   <td>
//                     <button onClick={() => viewChallan(item.DeliveryChallanNo)}>
//                       View
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* ✅ TEMPLATE PREVIEW */}
//       {selectedChallan && (
//         <div className="sales-table-card">
//           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
//             <h2>Challan Preview</h2>

//             <div style={{ display: "flex", gap: "10px" }}>
//               <button onClick={downloadPDF}>Download PDF</button>
//               <button onClick={handlePrint}>Print</button>
//               <button onClick={() => setSelectedChallan(null)}>Close</button>
//             </div>
//           </div>

//           <div ref={printRef}>
//             <DeliveryChallanTemplate challanData={selectedChallan} />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default DeliveryHistory;

// import React, { useEffect, useState } from "react";
// import api from "../api";
// import Sidebar from "../Components/Sidebar";
// import Topbar from "../Components/Topbar";

// import "../CSS/Deliverychallan.css"; // ✅ NEW: Import CSS for styling

// function DeliveryHistory() {
//   const [historyList, setHistoryList] = useState([]);
//   const [selectedChallan, setSelectedChallan] = useState(null);
//   const [challanProducts, setChallanProducts] = useState([]);
//   const [searchTerm, setSearchTerm] = useState(""); // ✅ NEW

//   useEffect(() => {
//     fetchHistory();
//   }, []);

//   const fetchHistory = async () => {
//     try {
//       const res = await api.get("/delivery/getDeliveryChallanHistory");
//       if (res.data.status) setHistoryList(res.data.data);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   const viewChallan = async (challanNo) => {
//     try {
//       const res = await api.get(`/delivery/getChallanByNumber/${challanNo}`);
//       if (res.data.status) {
//         setSelectedChallan(challanNo);
//         setChallanProducts(res.data.data);
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // ✅ FILTER LOGIC (global search across all fields)
//   const filteredHistory = historyList.filter((item) => {
//     const search = searchTerm.toLowerCase();

//     return (
//       item.DeliveryChallanNo?.toLowerCase().includes(search) ||
//       item.customer_name?.toLowerCase().includes(search) ||
//       item.created_date?.toLowerCase().includes(search) ||
//       item.total_items?.toString().includes(search) ||
//       item.total_quantity?.toString().includes(search)
//     );
//   });

//   return (
//     <div className="sales-page">
//       <Sidebar />
//       <Topbar />

//       <div className="sales-header">
//         <h1>Delivery Challan History</h1>
//       </div>

//       <div className="sales-table-card">
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//           <h2>History</h2>

//           {/* ✅ SEARCH INPUT (Top Right) */}
//           <input
//             type="text"
//             placeholder="Search..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             style={{
//               padding: "10px",
//               borderRadius: "5px",
//               border: "1px solid #ccc",
//               width: "300px"
//             }}
//           />
//         </div>

//         <table className="sales-table">
//           <thead>
//             <tr>
//               <th>Challan No</th>
//               <th>Customer</th>
//               <th>Date</th>
//               <th>Total Items</th>
//               <th>Total Quantity</th>
//               <th>Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {filteredHistory.length === 0 ? (
//               <tr>
//                 <td colSpan="6">No Challans Found</td>
//               </tr>
//             ) : (
//               filteredHistory.map((item, index) => (
//                 <tr key={index}>
//                   <td>{item.DeliveryChallanNo}</td>
//                   <td>{item.customer_name}</td>
//                   <td>{item.created_date}</td>
//                   <td>{item.total_items}</td>
//                   <td>{item.total_quantity}</td>
//                   <td>
//                     <button onClick={() => viewChallan(item.DeliveryChallanNo)}>
//                       View
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {selectedChallan && (
//         <div className="sales-table-card">
//           <h2>Challan Details: {selectedChallan}</h2>
//           <table className="sales-table">
//             <thead>
//               <tr>
//                 <th>Customer</th>
//                 <th>Product</th>
//                 <th>Quantity</th>
//                 <th>Date</th>
//               </tr>
//             </thead>
//             <tbody>
//               {challanProducts.length === 0 ? (
//                 <tr>
//                   <td colSpan="4">No Products Found</td>
//                 </tr>
//               ) : (
//                 challanProducts.map((item, index) => (
//                   <tr key={index}>
//                     <td>{item.customer_name}</td>
//                     <td>{item.product_name}</td>
//                     <td>{item.quantity}</td>
//                     <td>{item.created_date}</td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       )}
//     </div>
//   );
// }

// export default DeliveryHistory;










// import React, { useEffect, useState } from "react";
// import api from "../api";
// import Sidebar from "../Components/Sidebar";
// import Topbar from "../Components/Topbar";

// function DeliveryHistory() {
//   const [historyList, setHistoryList] = useState([]);
//   const [selectedChallan, setSelectedChallan] = useState(null);
//   const [challanProducts, setChallanProducts] = useState([]);

//   useEffect(() => {
//     fetchHistory();
//   }, []);

//   const fetchHistory = async () => {
//     try {
//       const res = await api.get("/delivery/getDeliveryChallanHistory");
//       if (res.data.status) setHistoryList(res.data.data);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   const viewChallan = async (challanNo) => {
//     try {
//       const res = await api.get(`/delivery/getChallanByNumber/${challanNo}`);
//       if (res.data.status) {
//         setSelectedChallan(challanNo);
//         setChallanProducts(res.data.data);
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   return (
//     <div className="sales-page">
//       <Sidebar />
//       <Topbar />

//       <div className="sales-header">
//         <h1>Delivery Challan History</h1>
//       </div>

//       <div className="sales-table-card">
//         <h2>History</h2>
//         <table className="sales-table">
//           <thead>
//             <tr>
//               <th>Challan No</th>
//               <th>Customer</th>
//               <th>Date</th>
//               <th>Total Items</th>
//               <th>Total Quantity</th>
//               <th>Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {historyList.length === 0 ? (
//               <tr>
//                 <td colSpan="6">No Challans Found</td>
//               </tr>
//             ) : (
//               historyList.map((item, index) => (
//                 <tr key={index}>
//                   <td>{item.DeliveryChallanNo}</td>
//                   <td>{item.customer_name}</td>
//                   <td>{item.created_date}</td>
//                   <td>{item.total_items}</td>
//                   <td>{item.total_quantity}</td>
//                   <td>
//                     <button onClick={() => viewChallan(item.DeliveryChallanNo)}>
//                       View
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {selectedChallan && (
//         <div className="sales-table-card">
//           <h2>Challan Details: {selectedChallan}</h2>
//           <table className="sales-table">
//             <thead>
//               <tr>
//                 <th>Customer</th>
//                 <th>Product</th>
//                 <th>Quantity</th>
//                 <th>Date</th>
//               </tr>
//             </thead>
//             <tbody>
//               {challanProducts.length === 0 ? (
//                 <tr>
//                   <td colSpan="4">No Products Found</td>
//                 </tr>
//               ) : (
//                 challanProducts.map((item, index) => (
//                   <tr key={index}>
//                     <td>{item.customer_name}</td>
//                     <td>{item.product_name}</td>
//                     <td>{item.quantity}</td>
//                     <td>{item.created_date}</td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       )}
//     </div>
//   );
// }

// export default DeliveryHistory;